import React, { useState, useEffect } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Pagination,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ButtonBase,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';
import db from '../../services/db';
import icoVerde from '../../assets/icones/ico_sinal_verde.png';
import icoCinza from '../../assets/icones/ico_sinal_cinza.png';
import { useConnectivity } from '../../contexts/ConnectivityProvider';
import { Edit, Visibility, Cancel } from '@mui/icons-material';
import { syncDataFromDB } from '../../services/sync/completeDataSync';
import { useAuth } from '../../contexts/AuthContext';

function ClientesList() {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'codigo', direction: 'desc' });
  const [filters, setFilters] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showNotification } = useNotification();
  const { isOnline } = useConnectivity();
  const { user } = useAuth();
   // permissões do vendedor
   const canEdit = user?.vendedorModel?.permissaoAlterarCliente;
   const canDelete = user?.vendedorModel?.permissaoDeletarCliente;


  console.log(user)
  // Busca os clientes ao montar o componente ou quando a rota mudar (ex.: após edição)
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const allClientes = await db.clientes.toArray();
        setClientes(allClientes);
        setFilteredClientes(allClientes);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
        showNotification('Erro ao carregar clientes.', 'error');
      }
    };

    fetchClientes();
  }, [location, showNotification]);

  // Eventos de online/offline
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Atualiza os clientes filtrados quando os filtros ou os clientes mudam
  useEffect(() => {
    const filtered = clientes.filter((cliente) =>
      Object.keys(filters).every((key) =>
        String(cliente[key] || '')
          .toLowerCase()
          .includes(filters[key]?.toLowerCase() || '')
      )
    );
    setFilteredClientes(filtered);
    setPage(1);
  }, [filters, clientes]);

  // Ordena a lista conforme o campo e direção escolhidos
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Paginação e mudança do número de registros por página
  const handlePageChange = (_, value) => setPage(value);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Filtro global baseado em busca (por id, nome, razão social, cpf/cnpj ou documento)
  const handleGlobalFilterChange = async (value) => {
    const searchTerm = value.toLowerCase();
    try {
      const allClientes = await db.clientes.toArray();
      const filtered = allClientes.filter((cliente) =>
        ['id', 'nome', 'razaoSocial', 'cpfCnpj', 'documento'].some((key) => {
          const fieldValue = cliente[key];
          if (fieldValue == null) return false;
          return String(fieldValue).toLowerCase().includes(searchTerm);
        })
      );
      setFilteredClientes(filtered);
      setPage(1);
    } catch (error) {
      console.error('Erro ao filtrar clientes:', error);
      showNotification('Erro ao buscar clientes no banco.', 'error');
    }
  };

  // Exclusão de cliente
  const handleDelete = async (id) => {
    const cliente = clientes.find((cliente) => cliente.id === id);
    if (cliente) {
      try {
        if (!isOnline) {
          // Marcação para exclusão offline; sincronize posteriormente
          setClientes((prev) =>
            prev.map((c) =>
              c.id === id ? { ...c, sincronismo: 'false', statusSic: false } : c
            )
          );
          await db.clientes.update(id, { sincronismo: 'false', statusSic: false });
          showNotification(
            `Cliente ${id} excluído offline. Sincronize quando estiver online para excluir permanentemente.`,
            'info'
          );
        } else {
          await db.clientes.update(id, { statusSic: false, sincronismo: 'false' });
          setClientes((prev) => prev.filter((c) => c.id !== id));
          showNotification(`Cliente ${id} excluído com sucesso!`, 'success');
          await syncDataFromDB(user);
        }
        setFilteredClientes((prev) => prev.filter((c) => c.id !== id));
      } catch (error) {
        console.error('Erro ao excluir cliente:', error);
        showNotification('Erro ao excluir cliente.', 'error');
      }
    }
  };

  const confirmDelete = (id) => setConfirmDialog({ open: true, id });
  const cancelDelete = () => setConfirmDialog({ open: false, id: null });
  const proceedDelete = () => {
    if (confirmDialog.id) handleDelete(confirmDialog.id);
    cancelDelete();
  };

  // Ordena e pagina os clientes para exibição
  const paginatedClientes = [...filteredClientes]
    .sort((a, b) => {
      // Primeiro, ordena pelo status de sincronismo
      if (a.sincronismo === b.sincronismo) {
        if (!sortConfig.key) return 0;
        const aValue = String(a[sortConfig.key] || '');
        const bValue = String(b[sortConfig.key] || '');
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      // Clientes com sincronismo 'false' (offline) aparecem primeiro
      return a.sincronismo === 'false' ? -1 : 1;
    })
    .slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box sx={{ padding: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" align="center" sx={{ marginBottom: 2 }}>
        Lista de Clientes
      </Typography>

      {/* Ações e filtros superiores */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          marginBottom: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/clientes/edit')}
          fullWidth={isMobile}
          sx={{ maxWidth: 200 }}
        >
          Novo Cliente
        </Button>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Registros por Página</InputLabel>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            label="Registros por Página"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Busca global */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 2,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="CODIGO/NOME/CPF/CNPJ"
          fullWidth
          onChange={(e) => handleGlobalFilterChange(e.target.value)}
          sx={{ maxWidth: 300 }}
        />
      </Box>

      {/* Tabela responsiva */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'codigo'}
                  direction={sortConfig.key === 'codigo' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('codigo')}
                >
                  Código
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'nome'}
                  direction={sortConfig.key === 'nome' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('nome')}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell>Cidade/Estado</TableCell>
                  <TableCell>Bairro</TableCell>
                  <TableCell>CEP</TableCell>
                </>
              )}
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {paginatedClientes.map((cliente) => {
    // 1) calcula se já está sincronizado
    const isSynchronized = cliente.sincronismo === 'true';
    // 2) bloqueia ações se estiver online e sincronizado
    const disableActions = isSynchronized && isOnline;

    return ( 
              <TableRow key={cliente.id}>
                <TableCell sx={{ py: 0.5 }}>{cliente.codigo}</TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  {cliente.rasaoSocial || cliente.nome}
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell sx={{ py: 0.5 }}>
                      {cliente.cidade} / {cliente.estado}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>{cliente.bairro}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{cliente.cep}</TableCell>
                  </>
                )}
                <TableCell sx={{ py: 0.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <div style={{textAlign:'center'}}>   
                      
                                          <img
                      src={cliente.sincronismo === 'true' ? icoVerde : icoCinza}
                      alt={cliente.online ? 'Online' : 'Offline'}
                      style={{ width: '20px', height: '20px',position:'center' }}
                    />
                      
                   
                    <br />
                    <strong>
                      {cliente.sincronismo === 'true'
                        ? 'SINCRONIZADO'
                        : 'OFFLINE'}
                    </strong>
                    </div>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Box
                     sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    {/* Botão Editar: ícone e texto clicáveis */}
                    <ButtonBase
                        disabled={!canEdit || disableActions}
                        onClick={() => navigate(`/clientes/edit/${cliente.id}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                          opacity: !canEdit || disableActions ? 0.5 : 1,
                          pointerEvents: !canEdit || disableActions ? 'none' : 'auto',
                        }}
                      >
                        <Edit fontSize="small" sx={{ color: 'orange' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'orange' }}>
                          EDITAR
                        </Typography>
                      </ButtonBase>

                    {/* Botão Consultar */}
                    <ButtonBase
                      onClick={() => navigate(`/clientes/view/${cliente.id}`)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1,
                      }}
                    >
                      <Visibility fontSize="small" sx={{ color: 'blue' }} />
                      <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                        CONSULTAR
                      </Typography>
                    </ButtonBase>

                    {/* Botão Deletar */}
                    <ButtonBase
                        disabled={!canDelete || disableActions}
                        onClick={() => confirmDelete(cliente.id)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                          opacity: !canDelete || disableActions ? 0.5 : 1,
                          pointerEvents: !canDelete || disableActions ? 'none' : 'auto',
                        }}
                      >
                        <Cancel fontSize="small" sx={{ color: 'red' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                          DELETAR
                        </Typography>
                      </ButtonBase>
                  </Box>
                </TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Pagination
          count={Math.ceil(filteredClientes.length / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Diálogo de confirmação para exclusão */}
      <Dialog open={confirmDialog.open} onClose={cancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isOffline
              ? 'Você está offline. A exclusão pode ser revertida.'
              : 'A exclusão não pode ser revertida. Tem certeza que deseja excluir este cliente?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancelar
          </Button>
          <Button onClick={proceedDelete} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientesList;
